<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import SimulatorNodePreview from './SimulatorNodePreview.vue';

const props = defineProps({
  simulator: { type: Object, required: true },
});

const { t } = useI18n();

const currentNode = computed(() => props.simulator.currentNode.value);
const nodeData = computed(() => currentNode.value?.data || {});
</script>

<template>
  <div
    class="flex w-80 shrink-0 flex-col gap-4 overflow-y-auto rounded-xl border border-n-weak bg-n-solid-2 p-4"
  >
    <div class="flex items-center justify-between">
      <p class="mb-0 text-heading-3 text-n-slate-12">
        {{ t('SOPS.SIMULATOR.TITLE') }}
      </p>
      <Button
        icon="i-lucide-rotate-ccw"
        :label="t('SOPS.SIMULATOR.RESET')"
        xs
        slate
        ghost
        @click="simulator.reset()"
      />
    </div>

    <SimulatorNodePreview
      v-if="currentNode"
      :node="currentNode"
      :show-branches="false"
      :show-feedback="false"
      :run-data="simulator.runDataFor(currentNode.id)"
    />

    <template v-if="simulator.isComplete.value">
      <p class="mb-0 flex items-center gap-1.5 text-sm text-n-teal-11">
        <span class="i-lucide-circle-check size-4" />
        {{ t('SOPS.SIMULATOR.COMPLETE') }}
      </p>
    </template>

    <template v-else-if="simulator.isBranching.value">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('SOPS.SIMULATOR.PICK_BRANCH') }}
      </p>
      <div class="flex flex-col gap-1.5">
        <Button
          v-for="branch in nodeData.branches"
          :key="branch.id"
          :disabled="!simulator.hasEdgeFor(branch.id)"
          sm
          slate
          faded
          justify="start"
          class="!h-auto whitespace-normal py-1.5 text-left"
          @click="simulator.advance(branch.id)"
        >
          <span class="min-w-0 whitespace-pre-wrap break-words">
            {{ branch.label || t('SOPS.BUILDER.PANEL.BRANCH_PLACEHOLDER') }}
          </span>
        </Button>
      </div>
    </template>

    <template v-else>
      <p
        v-if="simulator.isDeadEnd.value"
        class="mb-0 flex items-center gap-1.5 text-sm text-n-amber-11"
      >
        <span class="i-lucide-triangle-alert size-4" />
        {{ t('SOPS.SIMULATOR.DEAD_END') }}
      </p>
      <Button
        v-else
        :label="t('SOPS.SIMULATOR.NEXT')"
        trailing-icon
        icon="i-lucide-arrow-right"
        sm
        @click="simulator.advance()"
      />
    </template>

    <div v-if="currentNode" class="border-t border-n-strong pt-3">
      <TextArea
        v-model="simulator.runDataFor(currentNode.id).feedback"
        class="!h-auto shrink-0"
        :label="t('SOPS.SIMULATOR.FEEDBACK_LABEL')"
        :placeholder="t('SOPS.SIMULATOR.FEEDBACK_PLACEHOLDER')"
        auto-height
        min-height="5.25rem"
        max-height="none"
      />
    </div>
  </div>
</template>
